import React, { useCallback, useEffect, useRef, useState } from 'react';
import { joiResolver } from '@hookform/resolvers/joi';

import { makeStyles } from 'tss-react/mui';
import {Controller, FormProvider, useForm} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  ChangeRequestDialog,
  ChangeRequestForm,
  Checkbox,
  ContentTemplate,
  CustomSelect,
  DescriptionLabel,
  Divider,
  ErrorInfo,
  SectionHeader,
  useSnackbarHandler,
} from 'web-library';
import { useHistory } from 'react-router-dom';
import { FUNCTION_NAMES } from 'web-library/models/AuthorityFunctions';
import { FunctionModule } from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import FormHeader from '../../../../components/form-items/FormHeader';
import { mapApproversToDropdown, useApproverList } from '../../logic/useApproverList';
import FormTableDataGroup from './FormTableDataGroup';
import {
  CreateDataArchivalForm,
  createDataArchivalSchema,
} from './schema/CreateDataArchivalSchema';
import LoadingComponent from '../../../../components/LoadingComponent';
import { useFileUpload } from '../../logic/useFileUpload';
import RouteKey from '../../../../routes/key';
import { useGetDataArchivalFilterYear } from '../../../../api-refactored/data-archival/useGetDataArchivalFilterYear';
import { dropDownMappingPureArray, useAPIDropdown } from '../../../../hooks/dropdown';
import {
  DataArchivalTemplate,
  DataLevel,
  useGetDataArchivalTemplate,
} from '../../../../api-refactored/data-archival';
import { usePostCreateDataArchival } from '../../../../api-refactored/data-archival/usePostCreateDataArchival';
import { CompleteDataArchivalContent } from '../../../../api-refactored/data-archival/useGetDataArchivalDetail';
import { SelectChangeEvent } from '@mui/material';

const { admin } = RouteKey.child;
const { dataArchival } = RouteKey.admin.child;

type TemplateFormData = CreateDataArchivalForm & {
  template: DataArchivalTemplate[];
  selectedArrays: DataLevel[][];
};

const useStyles = makeStyles()((theme) =>
({
  userGroupDetails: {
    marginBottom: theme.spacing(2),
  },
  submitButtonContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(10),
  },
  backButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
  },
  input: {
    marginBottom: theme.spacing(4),
    width: '54%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  checkbox: {
    display: 'none',
  },
}));

const CreateDataArchival = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  // state
  const tempFormData = useRef<TemplateFormData>();
  const [openModal, setOpenModal] = useState(false);
  const [selectedArrays, setSelectedArrays] = useState<DataLevel[][]>([]);

  // callback
  const handleBack = useCallback(() => {
    history.push(`/${admin}/${dataArchival.route}`);
  }, [history]);

  // hook
  const { snackbarHandler } = useSnackbarHandler();
  const { data: templateData } = useGetDataArchivalTemplate({
    onSuccess: (template) => {
      if (selectedArrays.length === 0) {
        const initVal = template.map((e) => []);
        setSelectedArrays(initVal);
      }
    },
  });
  const { mutate: createDataArchival } = usePostCreateDataArchival({
    onSuccess: () => {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>DataArchival.ChangeRequestSuccessfullySentForApproval'),
      });
      handleBack();
    },
  });
  const filterYearResponse = useGetDataArchivalFilterYear({});
  const filterYear = useAPIDropdown(
    () => ({ data: filterYearResponse.data }),
    true,
    dropDownMappingPureArray
  );
  const { approverList, isLoading: approverLoading } = useApproverList({
    functionModule: FunctionModule.ADMIN,
    functionName: FUNCTION_NAMES.DATA_ARCHIVAL,
  });
  const {
    dropZone,
    isLoading: dropZoneLoading,
    uploadStatus,
    uploadFiles,
    fileEmptyAndRequired,
    emptyFileMessage,
  } = useFileUpload<TemplateFormData>({
    isUploadRequired: false,
    successCallback: (fileKeys, formData) => {
      const transformedContent: CompleteDataArchivalContent[] = formData.template.map(
        (item, idx) => {
          // normal multi select
          if (item.withDataLevel) {
            return {
              ...item,
              selectedDataLevel: formData.selectedArrays[idx].map((e) => e.uuid),
            };
          }
          // selected 'Yes' option
          return {
            ...item,
            selected: formData.selectedArrays[idx].length > 0,
          };
        }
      );

      createDataArchival({
        dataArchival: {
          examYear: formData.examYear,
          content: transformedContent,
        },
        requestCreationModel: {
          requestorRemarks: formData.remarks,
          approverUuid: formData.approver?.code,
          fileKeys,
        },
      });
    },
  });

  // create form
  const dataArchivalForm = useForm<CreateDataArchivalForm>({
    mode: 'onSubmit',
    resolver: joiResolver(createDataArchivalSchema()),
  });
  const { formState: { errors }, handleSubmit, setValue, register, control } = dataArchivalForm;

  // callback
  const handleExamYearChange = (e: SelectChangeEvent<unknown>) => {
    setValue('examYear', `${e.target.value}`);
  };
  const onSubmit = useCallback(
    (formData: CreateDataArchivalForm) => {
      tempFormData.current = {
        ...formData,
        template: templateData || [],
        selectedArrays,
      };
      if (!fileEmptyAndRequired()) {
        setOpenModal(true);
      }
    },
    [fileEmptyAndRequired, selectedArrays, templateData]
  );
  const onConfirm = useCallback(() => {
    setOpenModal(false);
    if (tempFormData.current) {
      uploadFiles(tempFormData.current);
    }
  }, [tempFormData, uploadFiles]);

  // effect
  useEffect(() => {
    const isEmptySelected = selectedArrays.every((item) => item.length === 0);
    setValue('archivalLevelSelect', !isEmptySelected);
  }, [selectedArrays, setValue]);

  // ui
  if (approverLoading || dropZoneLoading) {
    return <LoadingComponent isOpaque={false} />;
  }
  return (
    <FormProvider {...dataArchivalForm}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ContentTemplate isDividerBottom isContinuable>
          <SectionHeader title={t('admin->>DataArchival.DataArchivalDetails')} />
          <DescriptionLabel label={t('admin->>DataArchival.CreateDataArchivalInstruction')} />
          <Box className={classes.input}>
            <CustomSelect
              isRequired
              label={t('admin->>DataArchival.ExamYear')}
              variant="filled"
              displayEmpty
              defaultValue=""
              emptyItemValue=""
              placeholder={t('admin->>DataArchival.ExamYearPlaceholder')}
              inputProps={{
                inputRef: () => {
                  register('examYear');
                },
              }}
              menuItems={filterYear.functionCode}
              onChange={(e)=>handleExamYearChange(e)}
              error={!!errors.examYear?.message}
              helperText={errors.examYear?.message?.toString()}
            />
          </Box>
        </ContentTemplate>

        <ContentTemplate type="full" isDividerBottom isContinuable continuous>
          <FormHeader title={t('admin->>DataArchival.DataGroups')} />
          <DescriptionLabel label={t('admin->>DataArchival.DataGroupInstruction')} />
          <Controller
            name="archivalLevelSelect"
            control={control}
            render={(field)=>
              <Checkbox className={classes.checkbox} name="archivalLevelSelect" {...field} />
            }
          />

          <FormTableDataGroup
            template={templateData || []}
            editable
            setSelected={setSelectedArrays}
          />

          <Divider paddingTop />

          <ChangeRequestForm
            formHook={dataArchivalForm}
            approverDropdowns={mapApproversToDropdown(approverList)}
            isEditable
          />

          {dropZone}

          {errors?.archivalLevelSelect?.message !== '' && (
            <ErrorInfo label={errors?.archivalLevelSelect?.message} />
          )}

          {emptyFileMessage && <ErrorInfo label={emptyFileMessage || ''} />}

          <Box className={classes.submitButtonContainer}>
            <Button
              className={classes.backButton}
              variant="outlined"
              disabled={uploadStatus === 'loading'}
              onClick={handleBack}
            >
              {t('admin->>DataArchival.Back')}
            </Button>

            <ApiButton status={uploadStatus} type="submit">
              {t('admin->>DataArchival.ConfirmChanges')}
            </ApiButton>
          </Box>
        </ContentTemplate>

        <ChangeRequestDialog
          open={openModal}
          onClose={() => setOpenModal(false)}
          onConfirm={onConfirm}
        />
      </form>
    </FormProvider>
  );
};

export default CreateDataArchival;
